import { Link } from "react-router-dom";
import { RefreshCw, Newspaper, ShieldCheck } from "lucide-react";
import { ArticleCard } from "@/components/public/ArticleCard";
import { FactCheckCard } from "@/components/public/FactCheckCard";
// import { LiveUpdateCard } from "@/components/public/LiveUpdateCard";
// import { AvatarVideoCard } from "@/components/public/AvatarVideoCard";
import { FollowSection } from "@/components/public/FollowSection";
import { SectionHeader } from "@/components/public/SectionHeader";
import { useI18n } from "@/lib/i18n";
import { useArticles } from "@/hooks/useArticles";
import { useVerifications } from "@/hooks/useVerifications";
import { adaptArticle, adaptArticles, adaptVerifications } from "@/lib/adapters";
import type { Article, FactCheck } from "@/lib/mock-data";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { usePaysFiltre } from "@/lib/paysFilter";
import { useSeo } from "@/hooks/useSeo";

// ─── Squelettes de chargement ─────────────────────────────────────────────────

function ArticleCardSkeleton() {
  return (
    <div className="flex flex-col gap-3 animate-pulse">
      <div className="aspect-[16/9] rounded-md bg-muted" />
      <div className="h-3 w-16 rounded bg-muted" />
      <div className="space-y-1.5">
        <div className="h-4 rounded bg-muted" />
        <div className="h-4 w-3/4 rounded bg-muted" />
      </div>
      <div className="h-3 w-1/2 rounded bg-muted" />
    </div>
  );
}

function FactCheckCardSkeleton() {
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border bg-card p-5 animate-pulse">
      <div className="h-5 w-24 rounded bg-muted" />
      <div className="space-y-1.5">
        <div className="h-4 rounded bg-muted" />
        <div className="h-4 w-4/5 rounded bg-muted" />
      </div>
      <div className="h-3 rounded bg-muted" />
      <div className="h-3 w-2/3 rounded bg-muted" />
    </div>
  );
}

// ─── Bannière d'état vide ─────────────────────────────────────────────────────

function EmptyState({
  icon: Icon,
  message,
}: {
  icon: React.ElementType;
  message: string;
}) {
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
      <Icon className="h-8 w-8 opacity-25" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

// ─── Bannière d'erreur ────────────────────────────────────────────────────────

function ErrorBanner({ onRetry }: { onRetry: () => void }) {
  const { t } = useI18n();
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-10 gap-3">
      <p className="text-sm text-muted-foreground">{t("error.load")}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-semibold text-foreground hover:bg-surface transition-colors"
      >
        <RefreshCw className="h-3.5 w-3.5" /> {t("cta.retry")}
      </button>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function HomePage() {
  const { t, locale } = useI18n();
  const { paysIds } = usePaysFiltre();

  useSeo({
    title: locale === "fr"
      ? "Ramba News — Information vérifiée, sources protégées"
      : "Ramba News — Verified information, protected sources",
    description: locale === "fr"
      ? "Ramba News est la plateforme média numérique sécurisée dédiée à l'information vérifiée et au journalisme responsable en Afrique de l'Ouest."
      : "Ramba News is the secure digital media platform dedicated to verified information and responsible journalism in West Africa.",
    path: "/",
  });

  // Articles API (100 items pour avoir du contenu à filtrer côté client)
  const {
    data: articlesData,
    isLoading: loadingArticles,
    isError: errorArticles,
    refetch: refetchArticles,
  } = useArticles(0, 100, "PUBLIE", paysIds.length ? { paysIds } : undefined);

  // Vérifications API
  const {
    data: verificationsData,
    isLoading: loadingVerifs,
    isError: errorVerifs,
    refetch: refetchVerifs,
  } = useVerifications({ estPublic: true });

  // Adapter DTO → mock-shape
  const rawArticles: Article[] = adaptArticles(
    Array.isArray(articlesData)
      ? articlesData
      : articlesData?.content ?? []
  );

  const rawVerifications: FactCheck[] = adaptVerifications(
    Array.isArray(verificationsData) ? verificationsData : []
  );

  // Dérivés
  const latest = rawArticles.filter((a) => a.type !== "alert").slice(0, 4);
  const explainers = rawArticles.filter((a) => a.type === "explainer").slice(0, 3);
  const factChecks = rawVerifications.slice(0, 3);

  // const avatar1 = rawArticles[0];
  // const avatar2 = rawArticles[1];

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 md:py-12 space-y-16">

      {/* ── Hero : carrousel à gauche, 4 derniers articles en grille 2x2 à droite ── */}
      <section className="overflow-x-hidden">
        {loadingArticles ? (
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="aspect-[16/9] rounded-md bg-muted animate-pulse" />
            <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] sm:grid-cols-2 gap-3 sm:gap-6">
              {Array.from({ length: 4 }).map((_, i) => <ArticleCardSkeleton key={i} />)}
            </div>
          </div>
        ) : errorArticles ? (
          <ErrorBanner onRetry={refetchArticles} />
        ) : latest.length === 0 ? (
          <EmptyState icon={Newspaper} message={t("home.empty.articles")} />
        ) : (
          <div className="grid gap-8 lg:grid-cols-2 items-start">
            <Carousel opts={{ align: "start", loop: true }}>
              <CarouselContent>
                {latest.map((a) => (
                  <CarouselItem key={a.id}>
                    <ArticleCard article={a} variant="hero" />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-2 sm:-left-4" />
              <CarouselNext className="right-2 sm:-right-4" />
            </Carousel>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] sm:grid-cols-2 gap-3 sm:gap-6">
              {latest.map((a) => (
                <ArticleCard key={a.id} article={a} variant="compact" />
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ── Dernières informations ── */}
      <section className="overflow-x-hidden">
        <SectionHeader
          eyebrow={t("hero.verified")}
          title={t("section.latest")}
          action={
            <Link to="/search" className="text-xs font-semibold text-foreground hover:text-verified">
              {t("cta.readMore")} →
            </Link>
          }
        />

        <div className="mt-6">
          {loadingArticles ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => <ArticleCardSkeleton key={i} />)}
            </div>
          ) : errorArticles ? (
            <ErrorBanner onRetry={refetchArticles} />
          ) : latest.length === 0 ? (
            <EmptyState icon={Newspaper} message={t("home.empty.articles")} />
          ) : (
            <Carousel opts={{ align: "start", loop: true }}>
              <CarouselContent>
                {latest.map((a) => (
                  <CarouselItem key={a.id} className="md:basis-1/2 lg:basis-1/3">
                    <ArticleCard article={a} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-2 sm:-left-4" />
              <CarouselNext className="right-2 sm:-right-4" />
            </Carousel>
          )}
        </div>
      </section>

      {/* ── Fact-checks ── */}
      <section>
        <SectionHeader
          eyebrow={t("nav.factcheck")}
          title={t("section.factcheck")}
          action={
            <Link to="/factcheck" className="text-xs font-semibold text-foreground hover:text-verified">
              →
            </Link>
          }
        />
        <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {loadingVerifs ? (
            Array.from({ length: 3 }).map((_, i) => <FactCheckCardSkeleton key={i} />)
          ) : errorVerifs ? (
            <ErrorBanner onRetry={refetchVerifs} />
          ) : factChecks.length === 0 ? (
            <EmptyState icon={ShieldCheck} message={t("home.empty.verifications")} />
          ) : (
            factChecks.map((f) => <FactCheckCard key={f.id} fc={f} />)
          )}
        </div>
      </section>

    
      {/* ── Décryptages ── */}
      <section>
        <SectionHeader eyebrow={t("factcheck.filter.context")} title={t("section.explainers")} />
        <div className="mt-6 grid gap-8 md:grid-cols-3">
          {loadingArticles ? (
            Array.from({ length: 3 }).map((_, i) => <ArticleCardSkeleton key={i} />)
          ) : explainers.length === 0 ? (
            // Fallback : 3 derniers articles si pas d'explainer
            rawArticles.slice(1, 4).map((a) => (
              <ArticleCard key={a.id} article={a} variant="compact" />
            ))
          ) : (
            explainers.map((a) => <ArticleCard key={a.id} article={a} variant="compact" />)
          )}
        </div>
      </section>

      {/* ── Follow ── */}
      <FollowSection />
    </div>
  );
}

export default HomePage;